import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    Backdrop,
    CircularProgress,
    IconButton,
    Theme,
    Typography,
    makeStyles,
} from '@material-ui/core'
import Button from '@material-ui/core/Button'
import CloseIcon from '@material-ui/icons/Close'
import MoreVert from '@material-ui/icons/MoreVert'
import Skeleton from '@material-ui/lab/Skeleton'
import {
    AcceptAllTrackedChange,
    RejectAllTrackedChange,
} from 'api/graphQL/mutations/change.mutations'
import {
    GetAssebmlyElementChanges,
    GetFamilyElementChanges,
    GetProductTypeChanges,
} from 'api/graphQL/queries/changes.queries'
import {
    GetProjectNuggetContentItemNodes,
    GetProjectProductFamilyContentItemNodes,
} from 'api/graphQL/queries/projectElements.queries'
import {
    setElementTrackedChanges,
    workspaceSelectedElement,
} from 'api/graphQL/reactiveVars/element'
import { setIsAcceptAllRejectAll } from 'api/graphQL/reactiveVars/property'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    isContentTeam,
    resetOtherTabs,
    setIsAddProhibited,
    setWorkspaceContentItems,
    workspaceContents,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { workspaceLoadingState } from 'api/graphQL/reactiveVars/workspaceLoadingState'
import { workspaceToggles } from 'api/graphQL/reactiveVars/workspaceToggles'
import {
    GetProjectNuggetResponse,
    GetProjectProductFamilyResponse,
} from 'api/graphQL/types/projectElement.types'
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import { TrackedChange } from 'models/changes'
import React, { Suspense, useContext, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { getNestedObjectPropertyValue } from 'shared/utils/objectUtils'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import {
    CompleteMenuItemConfig,
    WorkspacePopover,
} from '../../components/WorkspacePopover'
import { useCustomTransition } from '../Common/hooks/useCustomTransition'
import { WorkspaceTabTitles } from '../Common/utils'
import { WorkspaceActionBarContext } from '../WorkspaceActionBarContext'
import { WorkspaceContentContext } from '../WorkspaceContentContext'
import { TrackChangesFilter } from './components/TrackChanges/TrackChangesFilter'
import { TrackChangesList } from './components/TrackChanges/TrackChangesList'
import { useShowRightMostPane } from './context/RightMostPaneContext'
import useBetaWorkspaceToggle from './hooks/useBetaWorkspaceToggle'

const LazyProgressDialog = React.lazy(
    () => import('@deltek/specification-client/core/Modal/ProgressDialog')
)

const defaultResolutionType = 'Filter by resolution type'

export interface TrackChangesStylesProps {
    isBetaWorkspaceToggleEnabled?: boolean
}

export const useStyles = makeStyles((theme: Theme) => ({
    actionLabel: {
        fontSize: pxToRem(13),
        color: '#185E8E',
    },
    moreIcon: {
        fill: '#185E8E',
    },
    formSidebar: (props: TrackChangesStylesProps) => ({
        minWidth: 240,
        height: props.isBetaWorkspaceToggleEnabled
            ? '100%'
            : 'calc(100% - 72px)',
        ...(props.isBetaWorkspaceToggleEnabled && {
            left: pxToRem(52),
            width: pxToRem(325),
            position: 'relative',
        }),
    }),
    titleGridItem: {
        width: pxToRem(270),
        height: '45px',
        display: 'flex',
        alignItems: 'center',
        background: '#1d49661a',
    },
    actionButtonGrid: (props: TrackChangesStylesProps) => ({
        width: props.isBetaWorkspaceToggleEnabled ? pxToRem(325) : '100%',
        ...(props.isBetaWorkspaceToggleEnabled && {
            height: pxToRem(16),
            paddingBottom: pxToRem(50),
        }),
    }),

    changesCard: {
        minWidth: pxToRem(234),
        maxWidth: pxToRem(234),
    },
    cardButton: {
        padding: 0,
    },
    changesList: {
        padding: (props: TrackChangesStylesProps) =>
            props.isBetaWorkspaceToggleEnabled
                ? `${0} ${pxToRem(14)} ${pxToRem(14)} ${pxToRem(14)}`
                : theme.spacing(1.75),
        paddingTop: '0 !important',
    },
    noChangesText: {
        textAlign: 'center',
        color: ' rgba(0, 0, 0, 0.5)',
        fontSize: pxToRem(14),
        width: pxToRem(270),
    },
    actionMenuItem: {
        padding: theme.spacing(0.5, 0),
    },
    changesGridItem: (props: TrackChangesStylesProps) => ({
        overflow: 'auto',
        marginTop: 0,
        top: props.isBetaWorkspaceToggleEnabled ? pxToRem(116) : pxToRem(95),
        position: 'absolute',
        bottom: pxToRem(8),
        ...(props.isBetaWorkspaceToggleEnabled && {
            maxWidth: pxToRem(325),
            gap: pxToRem(20),
        }),
        ...(props.isBetaWorkspaceToggleEnabled ? { width: '100%' } : {}),
    }),
    cardSkeleton: {
        marginLeft: theme.spacing(1.5),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    title: {
        fontWeight: 600,
        fontSize: pxToRem(16),
        color: '#314959',
        marginTop: pxToRem(4),
        paddingLeft: pxToRem(16),
    },
    actionsContainer: {
        justifyContent: 'space-between',
        paddingLeft: pxToRem(8),
        paddingRight: (props: TrackChangesStylesProps) =>
            props.isBetaWorkspaceToggleEnabled ? '0' : pxToRem(8),
    },
    toggleContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    toggleTitle: {
        fontWeight: 500,
        fontSize: 12,
        color: '#185E8E',
        paddingLeft: 5,
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.common.white,
    },
    closeButton: {
        paddingLeft: 5,
        color: '#185E8E',
        fontSize: 14,
    },
    filterContainer: {
        padding: '8px 12px',
        alignItems: 'center',
        width: pxToRem(325),
        height: pxToRem(30),
        top: pxToRem(33),
        borderRadius: pxToRem(4),
        borderWidth: pxToRem(1),
        justifyContent: 'space-between',
    },
    selectInput: {
        background: theme.palette.common.white,
        border: 'none',
        borderRadius: pxToRem(3.5),
        width: '100%',
        height: pxToRem(30),
        fontSize: pxToRem(12),
        '&:focus': {
            backgroundColor: theme.palette.common.white,
            outline: 'none',
        },
    },
    selectSelectOutlined: {
        padding: theme.spacing(1.5, 4, 1.25, 1),
    },
    filterDivider: {
        width: 325,
        borderBottom: '1px solid #0000001A',
        marginTop: 28,
    },
    endIcon: {
        marginRight: `${pxToRem(-20)} !important`,
    },
    filterOptionItem: {
        fontSize: pxToRem(12),
        fontFamily: 'Roboto',
        fontWeight: 400,
    },
    cardList: {
        listStyle: 'none',
        paddingLeft: 14,
        paddingRight: 14,
    },
    trackChangesWrapper: {
        height: '100%',
        width: '100%',
    },
}))

export interface TrackChangesPaneProps {
    selectedTopic?: Partial<ProductFamilyStudioManagement>,
    contentTeamFromStudioPage?: boolean
}

export const TrackChangesPane: React.FC<TrackChangesPaneProps> = ({
    selectedTopic,
    contentTeamFromStudioPage = false,
}) => {
    const { currentState, setStateValue } = React.useContext(
        WorkspaceActionBarContext
    )
    const isWorkspaceLoading = useReactiveVar(workspaceLoadingState)
    const betaWorkspaceToggle = useBetaWorkspaceToggle()
    const { previewFeatures } = usePreviewFeatures()
    const isBetaWorkspaceToggleEnabled = (!contentTeamFromStudioPage || previewFeatures.hasOMWorkspace) &&
        betaWorkspaceToggle
    const { showRightMostPane, setShowRightMostPane } = useShowRightMostPane()
    const classes = useStyles({
        isBetaWorkspaceToggleEnabled: isBetaWorkspaceToggleEnabled,
    })
    const [selectedResolutionType, setSelectedResolutionType] = React.useState<
        string
    >(defaultResolutionType)
    const [
        isAcceptRejectAllLoading,
        setIsAcceptRejectAllLoading,
    ] = React.useState(false)

    const actionRef = useRef<HTMLButtonElement | null>(null)
    const hasLoadedOnceRef = useRef(false)
    const { projectId } = useParams<{ projectId?: string }>()
    const {
        selectedElementNode: selectedProjectElementNode,
        trackedChanges,
    } = useReactiveVar(workspaceSelectedElement)
    const [_, startTransition] = useCustomTransition()

    const handleToggleSwitchValueChange = React.useCallback(
        (stateKey: string) => {
            setStateValue(
                stateKey,
                !getNestedObjectPropertyValue(stateKey, currentState),
                true
            )
        },
        [currentState, setStateValue]
    )
    const { isReviewer } = useReactiveVar(userInfo)
    const isContentTeamUser = useReactiveVar(isContentTeam)

    const { areSharedPropertiesVisible } = useReactiveVar(workspaceToggles)
    const [
        isActionPopoverDisplayed,
        setIsActionPopoverDisplayed,
    ] = React.useState(false)

    const { handleRetrieveUpdatedDTV } = useContext(WorkspaceContentContext)
    const [getAssemblyChanges, { loading: assemblyLoading }] = useLazyQuery(
        GetAssebmlyElementChanges,
        {
            displayName: 'GetAssemblyChanges',
            fetchPolicy: 'no-cache',
            onCompleted: (data) => {
                setElementTrackedChanges(
                    data?.getProjectUniformatClassification?.trackChanges
                )
            },
        }
    )

    const [getFamilyChanges, { loading: famLoading }] = useLazyQuery(
        GetFamilyElementChanges,
        {
            displayName: 'GetFamilyChanges',
            fetchPolicy: 'no-cache',
            onCompleted: (data) => {
                setElementTrackedChanges(
                    data?.getProjectProductFamily?.trackChanges
                )
            },
        }
    )

    const [getPTChanges, { loading: ptLoading }] = useLazyQuery(
        GetProductTypeChanges,
        {
            displayName: 'GetProductTypeChanges',
            fetchPolicy: 'no-cache',
            onCompleted: (data) => {
                setElementTrackedChanges(data?.getProjectNugget?.trackChanges)
            },
        }
    )
    const changes = React.useMemo(() => {
        if (
            isBetaWorkspaceToggleEnabled &&
            isWorkspaceLoading &&
            !hasLoadedOnceRef.current
        ) {
            return []
        }

        if (!trackedChanges || trackedChanges.length === 0) {
            return []
        }

        let filteredChanges = trackedChanges

        // Filter shared content items
        if (selectedProjectElementNode?.elementType === 'PRODUCTTYPE') {
            filteredChanges = filteredChanges.filter(
                (trackTip) =>
                    areSharedPropertiesVisible || !trackTip.isSharedContentItem
            )
        }

        // Filter by resolution type
        if (
            isBetaWorkspaceToggleEnabled &&
            selectedResolutionType &&
            selectedResolutionType !== defaultResolutionType
        ) {
            filteredChanges = filteredChanges.filter(
                (trackTip) => trackTip.status === selectedResolutionType
            )
        }

        return filteredChanges
    }, [
        trackedChanges,
        areSharedPropertiesVisible,
        selectedProjectElementNode?.elementType,
        selectedResolutionType,
        isBetaWorkspaceToggleEnabled,
        isWorkspaceLoading,
    ])

    const trackChangesMenuItems: CompleteMenuItemConfig[] = React.useMemo(
        () => [
            {
                dataTestId: 'acceptAllChangesButton',
                label: 'Accept All Changes',
                isDisabled: changes?.length === 0,
                onClick: () => {
                    handleAcceptOrRejectAllTrackChanges('accept')
                },
            },
            {
                dataTestId: 'rejectAllChangesButton',
                label: 'Reject All Changes',
                isDisabled: changes?.length === 0,
                onClick: () => {
                    handleAcceptOrRejectAllTrackChanges('reject')
                },
            },
        ],
        [changes]
    )
    const isProductOrFamilyElement = React.useMemo(() => {
        return (
            selectedProjectElementNode?.elementType !== 'ASSEMBLY' ||
            selectedTopic
        )
    }, [selectedProjectElementNode])

    const hasPendingActions = React.useMemo(() => {
        return !!changes?.find((item) => item.status === 'PENDING')
    }, [changes])

    const handleActionPopoverClick = React.useCallback(() => {
        setIsActionPopoverDisplayed(!isActionPopoverDisplayed)
    }, [isActionPopoverDisplayed])

    const { showGlobalSnackbar } = useGlobalSnackbar()

    const [getProjectProductFamily] = useLazyQuery<
        GetProjectProductFamilyResponse
    >(GetProjectProductFamilyContentItemNodes(6), {
        fetchPolicy: 'no-cache',
        onCompleted: (data: GetProjectProductFamilyResponse) => {
            setIsAcceptAllRejectAll(false)
            setIsAcceptRejectAllLoading(false)
            resetOtherTabs(true)
            startTransition(() => {
                setIsAddProhibited(false)
            }, 1000)
            setWorkspaceContentItems(
                data?.getProjectProductFamily?.contentItemNodes ?? []
            )
            startTransition(() => {
                resetOtherTabs(false)
            }, 100)
        },
        onError: (e) => {
            console.warn(
                summarizeApolloError(
                    e,
                    'There was a problem updating the workspace.'
                )
            )
        },
    })

    const [getSelectedNugget] = useLazyQuery<GetProjectNuggetResponse>(
        GetProjectNuggetContentItemNodes(6),
        {
            fetchPolicy: 'no-cache',
            displayName: 'GetProjectNugget',
            onCompleted: (data: GetProjectNuggetResponse) => {
                setIsAcceptAllRejectAll(false)
                setIsAcceptRejectAllLoading(false)
                resetOtherTabs(true)

                setWorkspaceContentItems(
                    data?.getProjectNugget?.contentItemNodes ?? []
                )
                startTransition(() => {
                    resetOtherTabs(false)
                }, 100)
            },
        }
    )
    const updateContentItemNodes = React.useCallback(() => {
        const selectedElementType = selectedProjectElementNode?.elementType
        if (
            selectedElementType !== ProjectElementType.ASSEMBLY ||
            selectedTopic
        ) {
            const { tabSelected, projectProductFamilyId } = workspaceContents()
            startTransition(() =>
                handleRetrieveUpdatedDTV?.(WorkspaceTabTitles[tabSelected ?? 0])
            )

            const queryInput = {
                variables: {
                    partType: isContentTeamUser
                        ? selectedElementType === ProjectElementType.PRODUCTTYPE
                            ? 'PRODUCTS'
                            : undefined // setting partype as undefined as workspace full view doesn't need parttype if Product Type is not selected in Element Tree
                        : WorkspaceTabTitles[tabSelected ?? 0],
                    elementId: selectedTopic
                        ? selectedTopic.ppfId
                        : selectedProjectElementNode?.elementId,
                    projectId: projectId as string,
                    productFamilyId: selectedTopic
                        ? selectedTopic.ppfId
                        : selectedElementType ===
                            ProjectElementType.PRODUCTFAMILY
                            ? selectedProjectElementNode?.elementId
                            : projectProductFamilyId,
                    familyNodeId: selectedTopic
                        ? selectedTopic.projectElementNodeId
                        : selectedElementType ===
                            ProjectElementType.PRODUCTFAMILY
                            ? selectedProjectElementNode?.id
                            : undefined,
                },
            }

            switch (selectedElementType) {
                case ProjectElementType.PRODUCTFAMILY:
                    getProjectProductFamily(queryInput)
                    break
                case ProjectElementType.PRODUCTTYPE:
                    getSelectedNugget(queryInput)
                    break
                case ProjectElementType.ASSEMBLY:
                    if (selectedTopic) {
                        getProjectProductFamily(queryInput)
                    }
                default:
                    break
            }
        }
    }, [
        getProjectProductFamily,
        getSelectedNugget,
        selectedProjectElementNode,
        projectId,
        isContentTeamUser,
    ])

    const handleResponseAcceptOrReject = React.useCallback(
        (trackChanges: TrackedChange[]) => {
            startTransition(() => {
                setIsActionPopoverDisplayed(false)
                setIsAcceptAllRejectAll(true)
                setElementTrackedChanges(trackChanges)
            })
            updateContentItemNodes()
        },
        [updateContentItemNodes]
    )

    const [acceptAllChange] = useMutation(AcceptAllTrackedChange, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) =>
            handleResponseAcceptOrReject(data.acceptAllTrackChanges),
        onError: (e) => {
            setIsAcceptRejectAllLoading(false)

            showGlobalSnackbar({
                variables: {
                    message: `There was a problem accepting all the changes.`,
                    variant: 'error',
                    id: 'CommentSection_AcceptChangesError',
                },
            })
        },
    })
    const [rejectAllChange] = useMutation(RejectAllTrackedChange, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) =>
            handleResponseAcceptOrReject(data.rejectAllTrackChanges),
        onError: (e) => {
            setIsAcceptRejectAllLoading(false)

            showGlobalSnackbar({
                variables: {
                    message: `There was a problem rejecting all the changes.`,
                    variant: 'error',
                    id: 'CommentSection_AcceptChangesError',
                },
            })
        },
    })

    const handleAcceptOrRejectAllTrackChanges = React.useCallback(
        (actionType: 'accept' | 'reject') => {
            if (
                selectedProjectElementNode &&
                isProductOrFamilyElement &&
                !isReviewer
            ) {
                const payload = {
                    variables: {
                        input: {
                            elementId: selectedTopic
                                ? selectedTopic.ppfId
                                : selectedProjectElementNode?.elementId,
                            projectId: projectId,
                            familyId:
                                selectedProjectElementNode?.elementType ===
                                    ProjectElementType.PRODUCTFAMILY
                                    ? selectedProjectElementNode?.elementId
                                    : workspaceContents()
                                        ?.projectProductFamilyId,
                        },
                        includeProductTypeInFamilyTrackChanges:
                            selectedProjectElementNode?.elementType ===
                            ProjectElementType.PRODUCTFAMILY,
                    },
                }

                startTransition(() => {
                    if (actionType == 'accept') {
                        acceptAllChange(payload)
                    } else {
                        rejectAllChange(payload)
                    }
                })
                setIsAcceptRejectAllLoading(true)
            }
        },
        [
            projectId,
            selectedProjectElementNode,
            isProductOrFamilyElement,
            isReviewer,
            hasPendingActions,
        ]
    )

    const handleClose = React.useCallback(() => {
        setShowRightMostPane((prevState) => (prevState ? false : prevState))
    }, [setShowRightMostPane])

    useEffect(() => {
        if (!hasLoadedOnceRef.current) {
            hasLoadedOnceRef.current = true
        }
    }, [])

    useEffect(() => {
        const canProceed =
            !isBetaWorkspaceToggleEnabled ||
            (isBetaWorkspaceToggleEnabled && !isWorkspaceLoading)

        if (!canProceed) return

        const elementId =
            selectedTopic?.ppfId || selectedProjectElementNode?.elementId

        if (!elementId) return

        let apiMethod

        if (
            !selectedTopic &&
            selectedProjectElementNode?.elementType === 'ASSEMBLY'
        ) {
            apiMethod = getAssemblyChanges
        } else if (
            selectedTopic ||
            selectedProjectElementNode?.elementType === 'PRODUCTFAMILY'
        ) {
            apiMethod = getFamilyChanges
        } else {
            apiMethod = getPTChanges
        }

        apiMethod({
            variables: {
                input: {
                    projectId,
                    elementId,
                },
                includeProductTypeInFamilyTrackChanges: !selectedTopic,
            },
        })
    }, [
        isWorkspaceLoading,
        selectedTopic?.ppfId,
        selectedProjectElementNode?.elementId,
        selectedProjectElementNode?.elementType,
        getAssemblyChanges,
        getFamilyChanges,
        getPTChanges,
        projectId,
        isBetaWorkspaceToggleEnabled,
    ])

    const isTrackChangesListLoading = assemblyLoading || famLoading || ptLoading

    const hasChangesData =
        ((isBetaWorkspaceToggleEnabled &&
            !isWorkspaceLoading &&
            hasLoadedOnceRef.current) ||
            !isBetaWorkspaceToggleEnabled) &&
        changes?.length! > 0 &&
        selectedProjectElementNode?.isInProject

    // A grid item cannot be a child of a list
    return (
        <>
            <GridContainer
                direction="column"
                className={classes.formSidebar}
                data-testid="trackChangesPane"
            >
                {!isBetaWorkspaceToggleEnabled && (
                    <GridItem className={classes.titleGridItem}>
                        <Typography className={classes.title} component="div">
                            TRACK CHANGES
                        </Typography>
                    </GridItem>
                )}
                <GridItem
                    className={classes.actionButtonGrid}
                    data-testid="buttonGridItem"
                >
                    <GridContainer className={classes.actionsContainer}>
                        <GridItem className={classes.toggleContainer}>
                            {isBetaWorkspaceToggleEnabled ? (
                                <Typography
                                    className={classes.toggleTitle}
                                    component="div"
                                >
                                    Track changes
                                </Typography>
                            ) : (
                                <>
                                    <ToggleSwitch
                                        checked={getNestedObjectPropertyValue(
                                            'showHideStates.changes',
                                            currentState
                                        )}
                                        onChange={() =>
                                            handleToggleSwitchValueChange(
                                                'showHideStates.changes'
                                            )
                                        }
                                        size={'small'}
                                    />
                                    <Typography
                                        className={classes.toggleTitle}
                                        component="div"
                                    >
                                        Show changes
                                    </Typography>
                                </>
                            )}
                        </GridItem>
                        <GridItem>
                            <Button
                                endIcon={
                                    <MoreVert className={classes.moreIcon} />
                                }
                                classes={{ text: classes.actionLabel }}
                                disableRipple
                                disableFocusRipple
                                ref={actionRef}
                                onClick={handleActionPopoverClick}
                                data-testid="moreActionButton"
                                aria-haspopup="true"
                                aria-expanded={isActionPopoverDisplayed}
                                aria-label="More actions button"
                            >
                                {!isBetaWorkspaceToggleEnabled && 'Actions'}
                            </Button>
                            {isBetaWorkspaceToggleEnabled && (
                                <IconButton
                                    className={classes.closeButton}
                                    data-testid="closePanel"
                                    onClick={handleClose}
                                    disableRipple
                                    aria-label="Close panel"
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            )}
                        </GridItem>
                    </GridContainer>
                </GridItem>
                {isBetaWorkspaceToggleEnabled && (
                    <>
                        <TrackChangesFilter
                            selectedResolutionType={selectedResolutionType}
                            setSelectedResolutionType={
                                setSelectedResolutionType
                            }
                            defaultResolutionType={defaultResolutionType}
                        />
                        <div
                            data-testid="filterDivider"
                            className={classes.filterDivider}
                        ></div>
                    </>
                )}
                <GridItem
                    data-testid="changesGridItem"
                    className={classes.changesGridItem}
                >
                    {isTrackChangesListLoading ? (
                        <>
                            <Skeleton
                                variant="rect"
                                width={310}
                                height={100}
                                className={classes.cardSkeleton}
                            />
                            <Skeleton
                                variant="rect"
                                width={310}
                                height={100}
                                className={classes.cardSkeleton}
                            />
                        </>
                    ) : hasChangesData ? (
                        //This list cannot be the container of a grid item
                        <TrackChangesList
                            changes={changes}
                            selectedTopic={selectedTopic}
                        />
                    ) : (
                        <Typography
                            className={classes.noChangesText}
                            data-testid="noChangesText"
                        >
                            No changes have been made
                        </Typography>
                    )}
                </GridItem>
            </GridContainer>

            <WorkspacePopover
                menuItems={trackChangesMenuItems}
                anchorElement={actionRef.current}
                isActionPopoverDisplayed={isActionPopoverDisplayed}
                handleActionPopoverClick={handleActionPopoverClick}
                anchorOrigin={{
                    vertical: (actionRef?.current?.clientHeight ?? 0) + 85,
                    horizontal: (actionRef?.current?.clientWidth ?? 0) - 10,
                }}
                disableReviewer={
                    !isProductOrFamilyElement ||
                    isReviewer ||
                    !hasPendingActions
                }
            />

            {isAcceptRejectAllLoading && (
                <Suspense
                    fallback={
                        <Backdrop className={classes.backdrop} open={true}>
                            <CircularProgress color="inherit" />
                        </Backdrop>
                    }
                >
                    <LazyProgressDialog
                        instanceId={'trackChanges'}
                        handleDialogClose={() => { }}
                        dialogDataTestId={'tackChangesProgressDialog'}
                        ariaLabelledBy={''}
                        title={'Resolving Changes'}
                        content={
                            'Sit tight while we resolve your track changes. The process will be done shortly...'
                        }
                        isActionComplete={false}
                    />
                </Suspense>
            )}
        </>
    )
}
